import { useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

const BLUR_DEBOUNCE_MS = 150;

export const useProctoringLogs = (
  examId: string,
  moduleId: string,
  studentId: string | undefined,
  enabled: boolean,
  onViolation?: () => void
) => {
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onViolationRef = useRef(onViolation);
  onViolationRef.current = onViolation;

  useEffect(() => {
    if (!enabled || !examId || !moduleId || !studentId) return;

    const logViolation = async (type: string, detail: string) => {
      onViolationRef.current?.();
      try {
        const examLogsRef = collection(
          db,
          'modules',
          moduleId,
          'tests',
          examId,
          'submissions',
          studentId,
          'exam-logs'
        );
        await addDoc(examLogsRef, {
          type,
          timestamp: serverTimestamp(),
          detail
        });
      } catch (err) {
        console.error('Failed to log proctoring event:', err);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        logViolation(
          'TAB_SWITCH',
          'User navigated away from the exam tab.'
        );
      }
    };

    const handleWindowBlur = () => {
      blurTimeoutRef.current = setTimeout(() => {
        blurTimeoutRef.current = null;
        if (!document.hasFocus()) {
          logViolation(
            'WINDOW_BLUR',
            'User switched away from exam window (e.g., another app, snipping tool, or side-by-side window).'
          );
        }
      }, BLUR_DEBOUNCE_MS);
    };

    const handleWindowFocus = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, [examId, moduleId, studentId, enabled]);
};
